import React, { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  Legend
} from 'recharts'
import { componentScope } from '../../lib/cssScope'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import { fetchTpsmAssessmentsForSupplier } from '../../mocks/fetchTpsmAssessmentsForSupplier'

type Assessment = {
  id: string
  standardCode: string
  status: string
  complianceScore: number
  assessedAt?: string
  updatedAt?: string
  createdAt?: string
  findings?: any
}

type Category = {
  code: string
  name: string
  complianceScore: number
  questionCount: number
  answeredCount: number
}

type Question = {
  id: string
  code: string
  text: string
  category: string
  answer?: any
  score?: number
}

export type SupplierStandardsExplorerPanelProps = {
  supplierId: string
  supplierName: string
  enabled?: boolean
  hideHeading?: boolean
}

const SCORE_COLORS: Record<string, string> = {
  "1": "#9ca3af",
  "2": "#ef4444",
  "3": "#3b82f6",
  "4": "#facc15",
  "5": "#10b981",
}

const clampPercent = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) return 0
  return Math.max(0, Math.min(100, Math.round(Number(value))))
}

const humanize = (value: string): string =>
  value
    .replace(/[_\s]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim()

export const SupplierStandardsExplorerPanel: React.FC<SupplierStandardsExplorerPanelProps> = ({
  supplierId,
  supplierName,
  enabled = true,
  hideHeading = false,
}) => {
  const { token } = useAuth()
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [questions, setQuestions] = useState<Question[]>([])
  
  // Filters
  const [selectedStandard, setSelectedStandard] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedScoreMin, setSelectedScoreMin] = useState<number>(0)
  const [selectedScoreMax, setSelectedScoreMax] = useState<number>(100)

  useEffect(() => {
    if (enabled && supplierId) {
      fetchData()
    }
  }, [enabled, supplierId, token])

  const fetchData = async () => {
    setLoading(true)
    try {
      const raw = await fetchTpsmAssessmentsForSupplier(supplierId, token)
      const loadedAssessments = Array.isArray(raw) ? raw : []
      setAssessments(loadedAssessments as Assessment[])

      // Extract categories and questions from assessments
      const categoriesMap = new Map<string, Category>()
      const questionsList: Question[] = []

      loadedAssessments.forEach((assessment: Assessment) => {
        if (assessment.findings?.metadata?.categories) {
          Object.entries(assessment.findings.metadata.categories).forEach(([catCode, catData]: [string, any]) => {
            if (!categoriesMap.has(catCode)) {
              categoriesMap.set(catCode, {
                code: catCode,
                name: catData.name || humanize(catCode),
                complianceScore: 0,
                questionCount: 0,
                answeredCount: 0
              })
            }
            const category = categoriesMap.get(catCode)!
            category.questionCount += catData.questions?.length || 0
            category.answeredCount += catData.answeredCount || 0
            category.complianceScore = Math.max(category.complianceScore, catData.complianceScore || 0)
          })
        }

        if (assessment.findings?.savedAnswers) {
          Object.entries(assessment.findings.savedAnswers).forEach(([questionId, answer]: [string, any]) => {
            questionsList.push({
              id: questionId,
              code: questionId,
              text: answer.questionText || questionId,
              category: answer.categoryCode || 'unknown',
              answer: answer.value,
              score: answer.score
            })
          })
        }
      })

      setCategories(Array.from(categoriesMap.values()))
      setQuestions(questionsList)
    } catch (e: any) {
      console.error('Failed to fetch data:', e)
      setAssessments([])
      setCategories([])
      setQuestions([])
    } finally {
      setLoading(false)
    }
  }

  // Get unique standards (deduplicated)
  const availableStandards = useMemo(() => {
    const standardsSet = new Set<string>()
    assessments.forEach(a => {
      if (a.standardCode) {
        standardsSet.add(a.standardCode)
      }
    })
    return Array.from(standardsSet).sort()
  }, [assessments])

  // Get available categories for selected standard
  const availableCategories = useMemo(() => {
    if (!selectedStandard) return []
    const standardCategories = new Set<string>()
    assessments
      .filter(a => a.standardCode === selectedStandard)
      .forEach(a => {
        if (a.findings?.metadata?.categories) {
          Object.keys(a.findings.metadata.categories).forEach(catCode => {
            standardCategories.add(catCode)
          })
        }
      })
    return Array.from(standardCategories).sort()
  }, [assessments, selectedStandard])

  // Filter assessments
  const filteredAssessments = useMemo(() => {
    return assessments.filter(a => {
      if (selectedStandard && a.standardCode !== selectedStandard) return false
      if (selectedStatus !== 'all' && a.status !== selectedStatus) return false
      const score = a.complianceScore || 0
      if (score < selectedScoreMin || score > selectedScoreMax) return false
      return true
    })
  }, [assessments, selectedStandard, selectedStatus, selectedScoreMin, selectedScoreMax])

  // Filter categories
  const filteredCategories = useMemo(() => {
    let cats = categories
    if (selectedStandard) {
      cats = cats.filter(c => {
        return assessments
          .filter(a => a.standardCode === selectedStandard)
          .some(a => a.findings?.metadata?.categories?.[c.code])
      })
    }
    if (selectedCategory !== 'all') {
      cats = cats.filter(c => c.code === selectedCategory)
    }
    return cats
  }, [categories, assessments, selectedStandard, selectedCategory])

  // Compliance by Standard data
  const complianceByStandardData = useMemo(() => {
    const seen = new Map<string, { code: string; name: string; value: number }>()
    filteredAssessments.forEach((assessment) => {
      const code = assessment.standardCode
      const value = clampPercent(assessment.complianceScore || 0)
      if (!seen.has(code) || seen.get(code)!.value < value) {
        seen.set(code, {
          code,
          name: humanize(code),
          value
        })
      }
    })
    return Array.from(seen.values()).sort((a, b) => a.value - b.value)
  }, [filteredAssessments])

  // Radar data (maturity map)
  const radarData = useMemo(() => {
    return filteredCategories.map(cat => ({
      subject: cat.name.length > 15 ? cat.name.substring(0, 12) + '...' : cat.name,
      fullName: cat.name,
      value: cat.complianceScore
    }))
  }, [filteredCategories])

  // Weak areas
  const weakAreas = useMemo(() => {
    return [...filteredCategories]
      .sort((a, b) => a.complianceScore - b.complianceScore)
      .slice(0, 5)
      .map(item => ({
        name: item.name.length > 25 ? item.name.substring(0, 22) + '...' : item.name,
        fullName: item.name,
        value: item.complianceScore
      }))
  }, [filteredCategories])

  // Assessment timeline
  const timelineData = useMemo(() => {
    const sorted = [...filteredAssessments]
      .filter(a => a.assessedAt || a.updatedAt || a.createdAt)
      .sort((a, b) => {
        const dateA = new Date(a.assessedAt || a.updatedAt || a.createdAt || 0).getTime()
        const dateB = new Date(b.assessedAt || b.updatedAt || b.createdAt || 0).getTime()
        return dateA - dateB
      })
    
    const monthly: Record<string, { count: number; total: number }> = {}
    sorted.forEach(a => {
      const date = new Date(a.assessedAt || a.updatedAt || a.createdAt || 0)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!monthly[monthKey]) {
        monthly[monthKey] = { count: 0, total: 0 }
      }
      monthly[monthKey].count++
      monthly[monthKey].total += a.complianceScore || 0
    })
    
    return Object.entries(monthly)
      .map(([key, data]) => ({
        label: key,
        value: Math.round(data.total / data.count)
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredAssessments])

  const formatPercent = (value: number) => `${clampPercent(value)}%`

  const CHART_PX = 288
  const chartCardClass =
    'ds-card flex w-[min(100%,400px)] min-w-[280px] shrink-0 flex-col p-4 sm:min-w-[320px]'
  const chartScrollClass = 'min-h-[288px] w-full min-w-0 overflow-auto'

  if (!enabled) return null

  return (
    <div className={componentScope('SupplierStandardsExplorerPanel')}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600 mx-auto mb-2" />
                <span className="text-sm text-gray-600">{t('common.loading', 'Loading...')}</span>
              </div>
            </div>
          ) : (
            <>
              {!hideHeading && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-[var(--ds-text,#111827)]">
                    {t('tpsm.supplierProfile.standardsExplorer', 'Standards Explorer')}
                    {supplierName ? ` — ${supplierName}` : ''}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--ds-text-secondary,#6b7280)]">
                    {t(
                      'tpsm.supplierProfile.standardsExplorerDesc',
                      'Explore compliance by standard, category, and question',
                    )}
                  </p>
                </div>
              )}

              {/* Filters */}
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
                <Select
                  label={t('compliance.filters.standard', 'Standard')}
                  options={[
                    { value: '', label: t('compliance.filters.allStandards', 'All Standards') },
                    ...availableStandards.map((std) => ({
                      value: std,
                      label: humanize(std),
                    })),
                  ]}
                  value={selectedStandard}
                  onChange={(v) => setSelectedStandard(v ?? '')}
                  placeholder={t('compliance.filters.standard', 'Standard')}
                  minWidth="100%"
                />

                {availableCategories.length > 0 && (
                  <Select
                    label={t('compliance.filters.category', 'Category')}
                    options={[
                      { value: 'all', label: t('compliance.filters.allCategories', 'All Categories') },
                      ...availableCategories.map((cat) => ({ value: cat, label: humanize(cat) })),
                    ]}
                    value={selectedCategory}
                    onChange={(v) => v != null && setSelectedCategory(v)}
                    placeholder={t('compliance.filters.category', 'Category')}
                    minWidth="100%"
                  />
                )}

                <Select
                  label={t('tpsm.filters.status', 'Status')}
                  options={[
                    { value: 'all', label: t('tpsm.filters.allStatuses', 'All Statuses') },
                    { value: 'NOT_STARTED', label: t('tpsm.status.notStarted', 'Not Started') },
                    { value: 'IN_PROGRESS', label: t('tpsm.status.inProgress', 'In Progress') },
                    { value: 'COMPLETED', label: t('tpsm.status.completed', 'Completed') },
                    { value: 'REVIEWED', label: t('tpsm.status.reviewed', 'Reviewed') },
                  ]}
                  value={selectedStatus}
                  onChange={(v) => v != null && setSelectedStatus(v)}
                  placeholder={t('tpsm.filters.status', 'Status')}
                  minWidth="100%"
                />

                <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
                  <div className="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-3">
                    <TextField
                      label={t('tpsm.filters.scoreMin', 'Min score')}
                      type="number"
                      min={0}
                      max={100}
                      value={String(selectedScoreMin)}
                      onChange={(v) => setSelectedScoreMin(Number(v) || 0)}
                      minWidth="5rem"
                    />
                    <TextField
                      label={t('tpsm.filters.scoreMax', 'Max score')}
                      type="number"
                      min={0}
                      max={100}
                      value={String(selectedScoreMax)}
                      onChange={(v) => setSelectedScoreMax(Number(v) || 0)}
                      minWidth="5rem"
                    />
                  </div>
                </div>
              </div>

              {/* Charts row — same pattern as Compliance Summary */}
              <div className="charts-row mb-6 w-full min-w-0">
                {/* Compliance by Standard */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.byStandard", "Compliance by Standard")}
                  </h2>
                  <div className={chartScrollClass}>
                    {complianceByStandardData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <BarChart data={complianceByStandardData} layout="vertical" margin={{ top: 12, right: 24, bottom: 8, left: 35 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Bar dataKey="value" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Maturity Map */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.maturityMap", "Control Maturity Map")}
                  </h2>
                  <div className={chartScrollClass}>
                    {radarData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <Radar
                            name="Compliance"
                            dataKey="value"
                            stroke="#3b82f6"
                            fill="#3b82f6"
                            fillOpacity={0.6}
                          />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Weak Areas */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.weakAreas", "Weak Areas (Lowest 5)")}
                  </h2>
                  <div className={chartScrollClass}>
                    {weakAreas.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <BarChart data={weakAreas} layout="vertical" margin={{ top: 12, right: 24, bottom: 8, left: 80 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Bar dataKey="value" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* Assessment Timeline */}
                <div className={chartCardClass}>
                  <h2 className="mb-3 text-base font-semibold text-[var(--ds-text,#111827)]">
                    {t("compliance.charts.timeline", "Assessment Timeline")}
                  </h2>
                  <div className={chartScrollClass}>
                    {timelineData.length === 0 ? (
                      <div className="flex h-[288px] items-center justify-center text-sm text-gray-500">
                        {t("common.noData", "No data")}
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={CHART_PX}>
                        <LineChart data={timelineData} margin={{ top: 12, right: 24, bottom: 8, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value: number) => formatPercent(value)} />
                          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Table */}
              <div className="ds-card p-4 mb-6">
                <h2 className="text-base font-semibold text-[var(--ds-text,#111827)] mb-4">
                  {t('tpsm.supplierProfile.categoryTable', 'Category Table')}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('tpsm.table.category', 'Category')}</th>
                        <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('tpsm.table.compliance', 'Compliance')}</th>
                        <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('tpsm.table.questions', 'Questions')}</th>
                        <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('tpsm.table.answered', 'Answered')}</th>
                        <th className="text-left py-2 px-4 font-semibold text-gray-700">{t('tpsm.table.progress', 'Progress')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-gray-500">
                            {t('common.noData', 'No data')}
                          </td>
                        </tr>
                      ) : (
                        filteredCategories.map((category) => (
                          <tr key={category.code} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-4">{category.name}</td>
                            <td className="py-2 px-4">
                              <span className={`px-2 py-1 rounded text-xs ${
                                category.complianceScore >= 80 ? 'bg-green-100 text-green-700' :
                                category.complianceScore >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {category.complianceScore}%
                              </span>
                            </td>
                            <td className="py-2 px-4">{category.questionCount}</td>
                            <td className="py-2 px-4">{category.answeredCount}</td>
                            <td className="py-2 px-4">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    category.questionCount > 0 && (category.answeredCount / category.questionCount) >= 0.8 ? 'bg-green-500' :
                                    category.questionCount > 0 && (category.answeredCount / category.questionCount) >= 0.5 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${category.questionCount > 0 ? (category.answeredCount / category.questionCount) * 100 : 0}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Question List */}
              <div className="ds-card p-4">
                <h2 className="text-base font-semibold text-[var(--ds-text,#111827)] mb-4">
                  {t('tpsm.supplierProfile.questionList', 'Question List')}
                </h2>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {questions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {t('common.noData', 'No data')}
                    </div>
                  ) : (
                    questions.map((question) => (
                      <div key={question.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{question.text}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {t('tpsm.question.category', 'Category')}: {humanize(question.category)}
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            {question.answer !== undefined && question.answer !== null ? (
                              <div className="text-sm text-gray-700">
                                {typeof question.answer === 'object' ? JSON.stringify(question.answer) : String(question.answer)}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">{t('tpsm.question.notAnswered', 'Not answered')}</span>
                            )}
                            {question.score !== undefined && (
                              <div className="text-xs text-gray-500 mt-1">
                                {t('tpsm.question.score', 'Score')}: {question.score}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
    </div>
  )
}

